/**
 * Shared warehouse roster chrome (WSRADM-0013 / WSRADM-0018). Pages own fetching;
 * this component renders title, search/date toolbar, table, empty/error/loading, and
 * pagination. Search (and optional from/to / include-voids) apply on submit unless
 * `searchMode` is `'liveMin2'` (Users).
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { ResourceStatus } from '@/hooks/useResource';
import { useCopy } from '@/hooks/useCopy';
import { ErrorDetails } from '@/components/ErrorDetails';

export interface RosterApplyQuery {
  search: string;
  from: string;
  to: string;
  includeVoids: boolean;
}

export interface RosterColumn<T> {
  id: string;
  header: string;
  render: (row: T) => ReactNode;
  sortId?: string;
  /** First-click order for a new column. Defaults to `asc` (Users / most keys). */
  defaultOrder?: 'asc' | 'desc';
  nowrap?: boolean;
}

export interface RosterTableProps<T> {
  title: string;
  columns: readonly RosterColumn<T>[];
  rows: readonly T[];
  rowId: (row: T) => string;
  status: ResourceStatus;
  /** The load error, shown with copyable details when `status` is `'error'`. */
  error?: unknown;
  totalElements: number;
  page: number;
  size: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (size: number) => void;
  onApply: (query: RosterApplyQuery) => void;
  onRetry: () => void;
  showDates?: boolean;
  showIncludeVoids?: boolean;
  initialFrom?: string;
  initialTo?: string;
  toolbarExtra?: ReactNode;
  searchMode?: 'submit' | 'liveMin2';
  sort?: string;
  order?: 'asc' | 'desc';
  onSortChange?: (id: string, nextOrder: 'asc' | 'desc') => void;
}

const nowrapSx = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: (theme: Theme) => theme.spacing(28),
} as const;

export function RosterTable<T>({
  title,
  columns,
  rows,
  rowId,
  status,
  error,
  totalElements,
  page,
  size,
  onPageChange,
  onRowsPerPageChange,
  onApply,
  onRetry,
  showDates = false,
  showIncludeVoids = false,
  initialFrom = '',
  initialTo = '',
  toolbarExtra,
  searchMode = 'submit',
  sort,
  order = 'asc',
  onSortChange,
}: RosterTableProps<T>) {
  const { t } = useCopy();
  const [searchDraft, setSearchDraft] = useState('');
  const [fromDraft, setFromDraft] = useState(initialFrom);
  const [toDraft, setToDraft] = useState(initialTo);
  const [includeVoidsDraft, setIncludeVoidsDraft] = useState(false);
  const liveMin2 = searchMode === 'liveMin2';

  const emitApply = (rawSearch: string) => {
    const trimmed = rawSearch.trim();
    onApply({
      search: liveMin2 && trimmed.length < 2 ? '' : trimmed,
      from: fromDraft,
      to: toDraft,
      includeVoids: includeVoidsDraft,
    });
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    emitApply(searchDraft);
  };

  const handleSort = (sortId: string) => {
    if (!onSortChange) return;
    if (sort === sortId) {
      onSortChange(sortId, order === 'asc' ? 'desc' : 'asc');
      return;
    }
    const column = columns.find((entry) => entry.sortId === sortId);
    onSortChange(sortId, column?.defaultOrder ?? 'asc');
  };

  return (
    <Box>
      <Typography variant="h1" sx={{ mb: 2.5 }}>
        {title}
      </Typography>
      <Stack
        component="form"
        direction="row"
        useFlexGap
        onSubmit={handleSubmit}
        sx={{ flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}
      >
        <TextField
          label={t('roster.search', 'Search')}
          value={searchDraft}
          onChange={(event) => {
            const next = event.target.value;
            setSearchDraft(next);
            if (liveMin2) emitApply(next);
          }}
          size="small"
        />
        {toolbarExtra}
        {showDates ? (
          <>
            <TextField
              type="date"
              label={t('roster.from', 'From')}
              value={fromDraft}
              onChange={(event) => setFromDraft(event.target.value)}
              size="small"
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              type="date"
              label={t('roster.to', 'To')}
              value={toDraft}
              onChange={(event) => setToDraft(event.target.value)}
              size="small"
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </>
        ) : null}
        {showIncludeVoids ? (
          <FormControlLabel
            control={
              <Checkbox
                checked={includeVoidsDraft}
                onChange={(_, checked) => setIncludeVoidsDraft(checked)}
              />
            }
            label={t('sales.includeVoids', 'Include voids')}
          />
        ) : null}
        {liveMin2 ? null : (
          <Button type="submit" variant="contained">
            {t('roster.searchApply', 'Apply')}
          </Button>
        )}
      </Stack>

      {status === 'loading' ? (
        <Skeleton variant="rounded" height={240} role="status" />
      ) : null}

      {status === 'error' ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              {t('common.retry', 'Retry')}
            </Button>
          }
        >
          {t('roster.loadError', 'Couldn’t load this list.')}
          <ErrorDetails
            intent="read"
            error={error}
            headline={t('roster.loadError', 'Couldn’t load this list.')}
          />
        </Alert>
      ) : null}

      {status === 'ready' ? (
        <>
          <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label={title}>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell key={column.id}>
                      {column.sortId ? (
                        <TableSortLabel
                          active={sort === column.sortId}
                          direction={
                            sort === column.sortId ? order : (column.defaultOrder ?? 'asc')
                          }
                          onClick={() => handleSort(column.sortId!)}
                        >
                          {column.header}
                        </TableSortLabel>
                      ) : (
                        column.header
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length}>{t('roster.empty', 'No rows.')}</TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow hover key={rowId(row)}>
                      {columns.map((column) => {
                        const content = column.render(row);
                        return (
                          <TableCell
                            key={column.id}
                            title={column.nowrap && typeof content === 'string' ? content : undefined}
                            sx={column.nowrap ? nowrapSx : undefined}
                          >
                            {content}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalElements}
            page={page}
            rowsPerPage={size}
            rowsPerPageOptions={[20, 50, 100]}
            onPageChange={(_, next) => onPageChange(next)}
            onRowsPerPageChange={(event) => onRowsPerPageChange(Number(event.target.value))}
          />
        </>
      ) : null}
    </Box>
  );
}
